/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { format, getDay, parse, startOfWeek } from "date-fns";
import { enIN } from "date-fns/locale";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import * as React from "react";
import { CalendarSkeleton } from "./calendar-skeleton";

const locales = {
	"en-IN": enIN,
};
dayjs.extend(timezone);

export interface HourMinutes {
	hour: number;
	minutes: number;
}

// Define View type locally to avoid importing react-big-calendar
export type ViewType = "month" | "week" | "work_week" | "day" | "agenda";

export interface CalendarPropsCustom<TEvent extends object> {
	endTime?: HourMinutes;
	startTime?: HourMinutes;
	EventWrapper?: React.ComponentType<any>;
	TimeSlotWrapper?: React.ComponentType;
	TimeHeader?: React.ComponentType;
	Toolbar?: React.ComponentType<any>;
	DateHeader?: React.ComponentType<any>;
	noSelectionOfPastDate?: boolean;
	disabled?: string[];
	[key: string]: unknown;
	onSelectSlot?: (slot: any) => void;
	events?: TEvent[];
	defaultDate?: Date;
	defaultView?: ViewType | "customWeek";
	selectable?: boolean;
	timeslots?: number;
	step?: number;
	views?: Record<string, React.ComponentType>;
	components?: Record<string, React.ComponentType>;
}

const MOBILE_LONG_PRESS_THRESHOLD_MS = 1; // Improve mobile long-press responsiveness for slot selection

function Calendar<TEvent extends object>({
	onSelectSlot,
	events = [],
	defaultDate = new Date(),
	defaultView = "week",
	selectable = true,
	endTime,
	startTime,
	EventWrapper,
	TimeSlotWrapper,
	TimeHeader,
	Toolbar,
	DateHeader,
	timeslots = 1,
	step = 60,
	noSelectionOfPastDate = false,
	views,
	components,
	disabled,
	...props
}: CalendarPropsCustom<TEvent>) {
	const [BigCalendar, setBigCalendar] = React.useState<typeof import("react-big-calendar") | null>(null);
	const [localizer, setLocalizer] = React.useState<import("react-big-calendar").DateLocalizer | null>(null);

	React.useEffect(() => {
		const loadBigCalendar = async () => {
			try {
				// Dynamic import of react-big-calendar
				const bigCalendarModule = await import("react-big-calendar");

				// Import CSS as a side effect only - ignore if it fails
				try {
					await import("react-big-calendar/lib/css/react-big-calendar.css");
				} catch {
					// CSS import failed, continue without it
				}

				const { dateFnsLocalizer } = bigCalendarModule;
				const calendarLocalizer = dateFnsLocalizer({
					format,
					parse,
					startOfWeek,
					getDay,
					locales,
				});

				setBigCalendar(bigCalendarModule);
				setLocalizer(calendarLocalizer);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error("Failed to load react-big-calendar:", error);
			}
		};

		loadBigCalendar();
	}, []);
	const min = React.useMemo(() => new Date(0, 0, 0, startTime?.hour ?? 9, startTime?.minutes ?? 0, 0), [startTime]);
	const max = React.useMemo(() => new Date(0, 0, 0, endTime?.hour ?? 20, endTime?.minutes ?? 0, 0), [endTime]);
	// Handle slot selection with validation
	const handleSelectSlot = React.useCallback(
		(slotInfo: any) => {
			if (noSelectionOfPastDate) {
				const now = new Date();
				if (slotInfo.start < now) {
					return;
				}
			}

			if (disabled && disabled.length > 0) {
				const slotDate = format(slotInfo.start, "yyyy-MM-dd");
				if (disabled.includes(slotDate)) {
					return;
				}
			}

			onSelectSlot?.(slotInfo);
		},
		[onSelectSlot, noSelectionOfPastDate, disabled],
	);

	if (!BigCalendar || !localizer) {
		return (
			<CalendarSkeleton
				min={new Date(0, 0, 0, startTime?.hour ?? 9, startTime?.minutes ?? 0, 0)}
				max={new Date(0, 0, 0, endTime?.hour ?? 20, endTime?.minutes ?? 0, 0)}
			/>
		);
	}

	const MainCalendar = BigCalendar.Calendar;
	const currentDefaultView = defaultView;

	return (
		<MainCalendar
			components={{
				timeSlotWrapper: TimeSlotWrapper,
				timeGutterHeader: TimeHeader,
				header: DateHeader,
				eventWrapper: EventWrapper,
				toolbar: Toolbar,
				...components,
			}}
			defaultDate={defaultDate}
			defaultView={currentDefaultView as ViewType}
			events={events}
			max={max}
			min={min}
			onSelectSlot={handleSelectSlot}
			selectable={selectable}
			step={step}
			timeslots={timeslots}
			views={views}
			localizer={localizer}
			longPressThreshold={MOBILE_LONG_PRESS_THRESHOLD_MS}
			{...props}
		/>
	);
}

export { UnitAvailabilityPage } from "./unit-availability-page";
export default Calendar;
